import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Formik, Form } from "formik";
import SubmitButton from "../../components/Forms/SubmitButton";
import SettingsSelect from "../../components/Forms/SettingsSelect";

const BedDetailModal = ({ bedId, yearId, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!bedId) return;
        setLoading(true);
        axios.get(`${process.env.REACT_APP_API_URL}reports/bed-details`, {
            params: { bed_id: bedId, year_id: yearId }
        }).then(res => {
            setData(res.data);
        }).catch(err => {
            console.error('Error loading bed details:', err);
        }).finally(() => setLoading(false));
    }, [bedId, yearId]);

    if (!bedId) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center px-6 py-4 border-b">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Bed Details
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
                </div>
                <div className="px-6 py-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                        </div>
                    ) : data ? (
                        <div>
                            <div className="mb-4 grid grid-cols-2 gap-2 text-sm">
                                <div><span className="font-medium text-gray-700">Bed:</span> {data.bed.bed_name}</div>
                                <div><span className="font-medium text-gray-700">Bed ID:</span> {data.bed.id}</div>
                                <div><span className="font-medium text-gray-700">Unit:</span> {data.bed.unit_name}</div>
                                <div><span className="font-medium text-gray-700">Unit ID:</span> {data.bed.unit_id}</div>
                                <div><span className="font-medium text-gray-700">Floor:</span> {data.bed.floor_name}</div>
                                <div><span className="font-medium text-gray-700">Property:</span> {data.bed.property_name}</div>
                                <div><span className="font-medium text-gray-700">Type:</span> {data.bed.unit_type}</div>
                                <div><span className="font-medium text-gray-700">Bed Status:</span> {data.bed.bed_status === 0 ? 'Available' : data.bed.bed_status === 1 ? 'Blocked (Red)' : data.bed.bed_status === 2 ? 'Blocked (Yellow)' : data.bed.bed_status}</div>
                            </div>

                            <h4 className="font-semibold text-gray-800 mt-4 mb-2">Bookings</h4>
                            {data.bookings.length === 0 ? (
                                <p className="text-sm text-gray-500 italic">No active bookings for this bed</p>
                            ) : (
                                <div className="space-y-3">
                                    {data.bookings.map(b => (
                                        <div key={b.booking_id} className="border rounded-lg p-3 text-sm">
                                            <div className="grid grid-cols-2 gap-1">
                                                <div><span className="font-medium text-gray-700">Tenant:</span> {b.tenant_name || 'N/A'}</div>
                                                <div><span className="font-medium text-gray-700">Folder:</span> <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-medium">{b.folder || 'N/A'}</span></div>
                                                <div><span className="font-medium text-gray-700">Booking ID:</span> {b.booking_id}</div>
                                                <div><span className="font-medium text-gray-700">User ID:</span> {b.user_id}</div>
                                                <div><span className="font-medium text-gray-700">Phone:</span> {b.phone || 'N/A'}</div>
                                                <div><span className="font-medium text-gray-700">Email:</span> {b.email || 'N/A'}</div>
                                                <div><span className="font-medium text-gray-700">Lease Signed:</span> {b.signed_lease_at || 'No'}</div>
                                                <div><span className="font-medium text-gray-700">Inspected:</span> {b.inspection_date || 'No'}</div>
                                            </div>
                                            <div className="mt-2">
                                                <a
                                                    href={`/booking/movedin?user_id=${b.user_id}&booking_id=${b.booking_id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-500 underline text-xs"
                                                >
                                                    View Booking
                                                </a>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="text-sm text-red-500">Failed to load bed details</p>
                    )}
                </div>
            </div>
        </div>
    );
};

const StatusBadge = ({ status }) => {
    const config = {
        unallocated: { color: "bg-green-200", label: "Unallocated", tooltip: "No booking assigned to this bed" },
        blue_bed: { color: "bg-blue-400 text-white", label: "New Lead", tooltip: "New lead with bed assigned (no signed lease yet)" },
        purple_bed: { color: "bg-purple-400 text-white", label: "Renewal", tooltip: "Renewal with bed assigned (no signed lease yet)" },
        black_bed: { color: "bg-gray-800 text-white", label: "Lease Signed", tooltip: "Lease signed / reserved" },
        pink_bed: { color: "bg-pink-400", label: "Moved In", tooltip: "Tenant has moved in (inspection done)" },
        red_block: { color: "bg-red-300", label: "Red Block", tooltip: "Bed blocked (red)" },
        yellow_block: { color: "bg-yellow-200", label: "Yellow Block", tooltip: "Bed blocked (yellow)" },
    };

    const { color, label, tooltip } = config[status] || { color: "bg-gray-200", label: status, tooltip: status };

    return (
        <span className={`px-2 py-1 rounded-full text-sm ${color}`} title={tooltip}>
            {label}
        </span>
    );
};

const BedList = ({ beds, showIds, onBedClick }) => {
    return (
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {beds.map((bed, idx) => (
                <div
                    key={`${bed.id}-${idx}`}
                    className={`flex items-center space-x-2 min-w-0 cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1 ${bed.duplicate ? 'ring-2 ring-orange-400 bg-orange-50 rounded-lg py-1' : ''}`}
                    onClick={() => onBedClick(bed.id)}
                    title={bed.duplicate ? `⚠ DUPLICATE: ${bed.duplicate_info.booking_count} bookings on this bed\nTenants: ${bed.duplicate_info.tenant_names}\nBooking IDs: ${bed.duplicate_info.booking_ids}` : `Click for details • bed id: ${bed.id}`}
                >
                    <div className="text-sm text-gray-500 flex-1 min-w-0">
                        <span className="inline-block truncate">
                            {bed.duplicate && <span className="text-orange-600 font-bold mr-1">⚠</span>}
                            <span>{bed.name}</span>{showIds && <span className="text-gray-300 text-xs"> (bed:{bed.id})</span>} • <span className="text-gray-400">{bed.type}</span>
                            {bed.duplicate && <span className="text-orange-600 text-xs font-medium ml-1">({bed.duplicate_info.booking_count} bookings)</span>}
                        </span>
                    </div>
                    <div className="flex-shrink-0">
                        <StatusBadge status={bed.status} />
                    </div>
                </div>
            ))}
        </div>
    );
};

const FloorSummary = ({ floor, propertyId, showIds, onBedClick }) => {
    const floorStats = floor.units.reduce((acc, unit) => {
        unit.beds.forEach(bed => {
            acc.total++;
            acc[bed.status] = (acc[bed.status] || 0) + 1;
        });
        return acc;
    }, { total: 0 });

    const duplicateCount = floor.units.reduce((acc, unit) => {
        const seen = new Set();
        unit.beds.forEach(bed => {
            if (bed.duplicate && !seen.has(bed.id)) {
                acc++;
                seen.add(bed.id);
            }
        });
        return acc;
    }, 0);

    const statItems = [
        { label: "Total", value: floorStats.total, color: "text-gray-500", tooltip: "Total beds" },
        { label: "New Leads", value: floorStats.blue_bed || 0, color: "text-blue-500", tooltip: "New leads with bed assigned" },
        { label: "Renewals", value: floorStats.purple_bed || 0, color: "text-purple-500", tooltip: "Renewals with bed assigned" },
        { label: "Lease Signed", value: floorStats.black_bed || 0, color: "text-gray-800", tooltip: "Lease signed / reserved" },
        { label: "Moved In", value: floorStats.pink_bed || 0, color: "text-pink-500", tooltip: "Tenant moved in (inspection done)" },
        { label: "Blocked", value: (floorStats.red_block || 0) + (floorStats.yellow_block || 0), color: "text-red-500", tooltip: "Red + Yellow blocked beds" },
        { label: "Unallocated", value: floorStats.unallocated || 0, color: "text-green-500", tooltip: "No booking assigned" },
        ...(duplicateCount > 0 ? [{ label: "⚠ Duplicates", value: duplicateCount, color: "text-orange-600 font-bold", tooltip: "Beds with multiple active bookings" }] : [])
    ];

    return (
        <div className="px-4 py-5 sm:p-6 border-b">
            <div className="flex flex-col space-y-2">
                <div className="flex justify-between items-center">
                    <h4 className="text-md font-medium text-gray-900">
                        {floor.name} ({floorStats.total} beds)
                        <a
                            className="ml-2 text-blue-500 underline"
                            href={`/properties/step2/${propertyId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            View Property
                        </a> |
                        <a
                            className="text-blue-500 underline"
                            href={`/properties/step3/${propertyId}/${floor.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            View Floor
                        </a>
                    </h4>
                </div>
                <div className="grid grid-cols-8 gap-4 text-sm">
                    {statItems.map(item => (
                        <div key={item.label} title={item.tooltip}>
                            <span className={item.color}>{item.label}: </span>
                            <span className="font-medium">{item.value}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-4">
                {floor.units.map((unit) => (
                    <div key={unit.id} className="pl-4 mt-4">
                        <h5 className="text-sm font-medium text-gray-900"><span title={`unit id: ${unit.id}`}>{unit.name}</span>{showIds && <span className="text-gray-400 text-xs font-normal"> (unit:{unit.id})</span>}</h5>
                        <BedList beds={unit.beds} showIds={showIds} onBedClick={onBedClick} />
                    </div>
                ))}
            </div>
        </div>
    );
};

const PropertySummary = ({ property, expanded, toggleExpand, showIds, onBedClick }) => {
    const statItems = [
        { label: "Total", value: property.summary.total, color: "text-gray-500", tooltip: "Total beds" },
        { label: "New Leads", value: property.summary.blue_bed || 0, color: "text-blue-500", tooltip: "New leads with bed assigned" },
        { label: "Renewals", value: property.summary.purple_bed || 0, color: "text-purple-500", tooltip: "Renewals with bed assigned" },
        { label: "Lease Signed", value: property.summary.black_bed || 0, color: "text-gray-800", tooltip: "Lease signed / reserved" },
        { label: "Moved In", value: property.summary.pink_bed || 0, color: "text-pink-500", tooltip: "Tenant moved in (inspection done)" },
        { label: "Blocked", value: (property.summary.red_block || 0) + (property.summary.yellow_block || 0), color: "text-red-500", tooltip: "Red + Yellow blocked beds" },
        { label: "Unallocated", value: property.summary.unallocated || 0, color: "text-green-500", tooltip: "No booking assigned" },
        ...((property.summary.duplicates || 0) > 0 ? [{ label: "⚠ Duplicates", value: property.summary.duplicates, color: "text-orange-600 font-bold", tooltip: "Beds with multiple active bookings - needs attention!" }] : [])
    ];

    return (
        <div className="bg-white shadow sm:rounded-lg">
            <div
                className="px-4 py-5 sm:p-6 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleExpand(`property-${property.id}`)}
            >
                <div className="flex items-center justify-between">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">{property.name}</h3>
                    <span className="transform transition-transform duration-200">
                        {expanded[`property-${property.id}`] ? "▼" : "▶"}
                    </span>
                </div>
                <div className="mt-2 grid grid-cols-8 gap-4">
                    {statItems.map(item => (
                        <div key={item.label} className="text-sm" title={item.tooltip}>
                            <span className={item.color}>{item.label}: </span>
                            <span className="font-medium">{item.value}</span>
                        </div>
                    ))}
                </div>
                {property.stored_bed_count !== null && property.stored_bed_count !== undefined && property.summary.total !== property.stored_bed_count && (
                    <div className="mt-2 bg-yellow-50 border border-yellow-300 rounded-lg px-4 py-2 text-sm text-yellow-800">
                        ⚠ <strong>Bed count mismatch:</strong> Report shows <strong>{property.summary.total}</strong> beds from the beds table, but property setting has <strong>{property.stored_bed_count}</strong> beds stored (difference of {Math.abs(property.summary.total - property.stored_bed_count)})
                    </div>
                )}
            </div>

            {expanded[`property-${property.id}`] && (
                <div className="border-t border-gray-200">
                    {property.floors.map((floor) => (
                        <FloorSummary
                            key={floor.id}
                            floor={floor}
                            propertyId={property.id}
                            showIds={showIds}
                            onBedClick={onBedClick}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const OverallSummary = ({ totals }) => {
    const summaryItems = [
        { label: "Total Beds", value: totals.total, className: "bg-gray-50", tooltip: "Total beds" },
        { label: "New Leads", value: totals.blue_bed || 0, className: "bg-blue-50", tooltip: "New leads with bed assigned" },
        { label: "Renewals", value: totals.purple_bed || 0, className: "bg-purple-50", tooltip: "Renewals with bed assigned" },
        { label: "Lease Signed", value: totals.black_bed || 0, className: "bg-gray-100", tooltip: "Lease signed / reserved" },
        { label: "Moved In", value: totals.pink_bed || 0, className: "bg-pink-50", tooltip: "Tenant moved in (inspection done)" },
        { label: "Blocked", value: (totals.red_block || 0) + (totals.yellow_block || 0), className: "bg-red-50", tooltip: "Red + Yellow blocked beds" },
        { label: "Unallocated", value: totals.unallocated || 0, className: "bg-green-50", tooltip: "No booking assigned" },
        ...((totals.duplicates || 0) > 0 ? [{ label: "⚠ Duplicates", value: totals.duplicates, className: "bg-orange-50 ring-2 ring-orange-400", tooltip: "Beds with multiple active bookings - needs attention!" }] : [])
    ];

    return (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Overall Summary</h3>
                {(totals.duplicates || 0) > 0 && (
                    <div className="mt-2 bg-orange-50 border border-orange-300 rounded-lg px-4 py-2 text-sm text-orange-800">
                        ⚠ <strong>{totals.duplicates} bed(s)</strong> have multiple active bookings assigned. These are highlighted in orange below. Click on a bed to see details.
                    </div>
                )}
                <div className="mt-4 grid grid-cols-8 gap-4">
                    {summaryItems.map(item => (
                        <div key={item.label} className={`${item.className} px-4 py-2 rounded-lg`} title={item.tooltip}>
                            <dt className="text-sm font-medium text-gray-500">{item.label}</dt>
                            <dd className="mt-1 text-2xl font-semibold text-gray-900">{item.value}</dd>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default function BedAllocationReport() {
    const [loading, setLoading] = useState(false);
    const [properties, setProperties] = useState([]);
    const [reportData, setReportData] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [years, setYears] = useState([]);
    const [showIds, setShowIds] = useState(false);
    const [selectedBedId, setSelectedBedId] = useState(null);
    const [selectedYearId, setSelectedYearId] = useState(null);
    const { current_staff } = useSelector((state) => state.auth);

    useEffect(() => {
        loadProperties();
        loadYears();
    }, []);

    const loadProperties = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}properties`);
            const allProperties = [
                { id: 0, label: "All Properties", value: 0 },
                ...response.data.map(prop => ({
                    id: prop.value,
                    label: prop.label,
                    value: prop.value,
                    name: prop.label,
                }))
            ];
            setProperties(allProperties);
        } catch (error) {
            console.error("Error loading properties:", error);
        }
    };

    const loadYears = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}get_years`);
            setYears(response.data);
        } catch (error) {
            console.error("Error loading years:", error);
        }
    };

    const toggleExpand = (key) => {
        setExpanded((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const handleGenerateReport = async (values, { setSubmitting }) => {
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}reports/bed-allocation`, {
                params: {
                    property_id: values.property_id === 0 ? null : values.property_id,
                    year_id: values.year_id,
                },
            });
            setReportData(response.data);
        } catch (error) {
            console.error("Error loading report:", error);
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    return (
        <div className="w-auto bg-gray-100 my-6 xl:my-0 xl:mx-4 rounded-3xl flex-1 p-5">
            <h1 className="text-2xl font-semibold text-gray-900 mb-6">Bed Allocation Report</h1>

            <Formik
                enableReinitialize
                initialValues={{
                    year_id: years.length > 0 ? years[years.length - 1].value : 3,
                    property_id: 0,
                }}
                onSubmit={handleGenerateReport}
            >
                {({ values, setFieldValue, handleSubmit, isValid, isSubmitting }) => (
                    <Form onSubmit={handleSubmit}>
                        <div className="grid lg:grid-cols-12 md:lg:grid-cols-6 gap-12 mt-5 mb-5">
                            <SettingsSelect
                                label="Year"
                                name="year_id"
                                onChange={setFieldValue}
                                value={values.year_id}
                                options={years}
                            />
                            <SettingsSelect
                                label="Select Building (Optional)"
                                name="property_id"
                                onChange={setFieldValue}
                                value={values.property_id}
                                options={properties}
                            />
                        </div>

                        <div className="flex items-center space-x-4">
                            <SubmitButton
                                name="Submit"
                                disabled={!isValid || isSubmitting}
                                isSubmitting={isSubmitting}
                                text="Generate Report"
                            />
                            <label className="flex items-center space-x-2 text-sm text-gray-600 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={showIds}
                                    onChange={(e) => setShowIds(e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span>Show IDs</span>
                            </label>
                        </div>

                        {loading && (
                            <div className="mt-4 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                                <span className="ml-2">Generating report...</span>
                            </div>
                        )}

                        {reportData && !loading && (
                            <div className="mt-6">
                                <OverallSummary totals={reportData.totals} />
                                <div className="mt-6 space-y-4">
                                    {reportData.properties.map((property) => (
                                        <PropertySummary
                                            key={property.id}
                                            property={property}
                                            expanded={expanded}
                                            toggleExpand={toggleExpand}
                                            showIds={showIds}
                                            onBedClick={(bedId) => { setSelectedBedId(bedId); setSelectedYearId(values.year_id); }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </Form>
                )}
            </Formik>

            <BedDetailModal
                bedId={selectedBedId}
                yearId={selectedYearId}
                onClose={() => setSelectedBedId(null)}
            />
        </div>
    );
}